import { calendarDaysUntil } from '@/lib/dates';
import { formatDateOnly } from '@/lib/formatters';
import { taskPriorityDisplay, taskStatusDisplay } from './enums';

/** Task as returned by GET /api/tasks (Prisma Task with assignee/client includes). */
export interface ApiTask {
  id: string;
  firmId: string;
  clientId: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  dueDate: string | null;
  assignedTo: string | null;
  createdBy: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  assignee: { id: string; name: string; email: string; avatar: string | null } | null;
  client: { id: string; name: string } | null;
}

/**
 * What the Tasks section renders. Shape-compatible with KanbanTask/TaskItem so the
 * board and detail panel accept it. `status` and `priority` are the raw API values;
 * `assigneeId`/`clientId` are kept for the Phase 3 write paths.
 */
export interface TaskView {
  id: string;
  title: string;
  description: string;
  /** Display date, '' when the task has no due date. */
  dueDate: string;
  /** ISO due date, null when the task has no due date. */
  dueDateIso: string | null;
  assignee: string;
  assigneeId: string | null;
  client: string;
  clientId: string | null;
  priority: string;
  priorityLabel: string;
  priorityColor: string;
  priorityBg: string;
  priorityDot: string;
  status: string;
  statusLabel: string;
  statusColor: string;
  statusBg: string;
  statusDot: string;
}

export const UNASSIGNED_LABEL = 'Unassigned';
export const NO_CLIENT_LABEL = '—';

/**
 * Due dates are stored as date-only values (UTC midnight), so they are formatted in UTC.
 * A writer that sends a datetime with a non-zero offset would show a day off: Phase 3 must send `YYYY-MM-DD`.
 */
function formatDueDate(iso: string | null): string {
  return formatDateOnly(iso) ?? '';
}

export function toTaskView(task: ApiTask): TaskView {
  const status = taskStatusDisplay(task.status);
  const priority = taskPriorityDisplay(task.priority);
  const hasDue = formatDueDate(task.dueDate) !== '';
  return {
    id: task.id,
    title: task.title,
    description: task.description ?? '',
    dueDate: formatDueDate(task.dueDate),
    dueDateIso: hasDue ? task.dueDate : null,
    assignee: task.assignee?.name ?? UNASSIGNED_LABEL,
    assigneeId: task.assignee?.id ?? task.assignedTo ?? null,
    client: task.client?.name ?? NO_CLIENT_LABEL,
    clientId: task.client?.id ?? task.clientId ?? null,
    priority: task.priority,
    priorityLabel: priority.label,
    priorityColor: priority.color,
    priorityBg: priority.bg,
    priorityDot: priority.dot,
    status: task.status,
    statusLabel: status.label,
    statusColor: status.color,
    statusBg: status.bg,
    statusDot: status.dot,
  };
}

const CLOSED_STATUSES: readonly string[] = ['completed', 'archived'];

export function isOpenTask(task: Pick<TaskView, 'status'>): boolean {
  return !CLOSED_STATUSES.includes(task.status);
}

export function isOverdue(task: Pick<TaskView, 'status' | 'dueDateIso'>, now: Date): boolean {
  if (!isOpenTask(task)) return false;
  const days = calendarDaysUntil(task.dueDateIso, now);
  return days !== null && days < 0;
}

export interface TaskStats {
  /** Total across the whole firm (from pagination), not just the loaded page. */
  total: number;
  /** The remaining figures are counted over the loaded tasks only. One per status, so they add up to the loaded tasks. */
  pending: number;
  inProgress: number;
  inReview: number;
  completed: number;
  archived: number;
  /** Open tasks due before today; a subset of the statuses above, not an extra status. */
  overdue: number;
  /** True when more tasks exist than were loaded, so the counts above are partial. */
  partial: boolean;
}

export function getTaskStats(tasks: readonly TaskView[], total: number, now: Date): TaskStats {
  return {
    total,
    pending: tasks.filter((t) => t.status === 'pending').length,
    inProgress: tasks.filter((t) => t.status === 'in_progress').length,
    inReview: tasks.filter((t) => t.status === 'in_review').length,
    completed: tasks.filter((t) => t.status === 'completed').length,
    archived: tasks.filter((t) => t.status === 'archived').length,
    overdue: tasks.filter((t) => isOverdue(t, now)).length,
    partial: total > tasks.length,
  };
}

export type DeadlineTone = 'overdue' | 'today' | 'soon' | 'later';

export interface UpcomingDeadline {
  id: string;
  title: string;
  date: string;
  urgency: string;
  tone: DeadlineTone;
}

function describeUrgency(days: number): { urgency: string; tone: DeadlineTone } {
  if (days < 0) return { urgency: days === -1 ? 'Overdue by 1 day' : `Overdue by ${-days} days`, tone: 'overdue' };
  if (days === 0) return { urgency: 'Due today', tone: 'today' };
  if (days === 1) return { urgency: 'Due tomorrow', tone: 'soon' };
  return { urgency: `In ${days} days`, tone: days <= 7 ? 'soon' : 'later' };
}

/** Open tasks that have a due date, soonest (or most overdue) first. */
export function getUpcomingDeadlines(tasks: readonly TaskView[], now: Date, limit = 5): UpcomingDeadline[] {
  return tasks
    .map((t) => ({ task: t, days: isOpenTask(t) ? calendarDaysUntil(t.dueDateIso, now) : null }))
    .filter((entry): entry is { task: TaskView; days: number } => entry.days !== null)
    .sort((a, b) => a.days - b.days)
    .slice(0, limit)
    .map(({ task, days }) => ({
      id: task.id,
      title: task.title,
      date: task.dueDate,
      ...describeUrgency(days),
    }));
}
